import React, { useContext, useEffect, useState } from "react";
import ModalMain from "../../../../components/Modal/ModalMain";
import ModalHeader from "../../../../components/Modal/ModalHeader";
import AiExportCsvBody from "./AiExportCsvBody";
import { useTranslation } from "react-i18next";
import ModalFooter from "../../../../components/Modal/ModalFooter";
import { useFormik } from "formik";
import { aiExportCsvStatusAPI, aiExportCsvInitiate } from "../../../../service/AIRequestsService";
import toastContext from "../../../../context/toast/ToastContext";
import { TOAST_TYPE } from "../../../../constants/Constants";

const AiExportCsvModal = (props) => {
  const { show, onClose, aiRequestsFilterObj, filterInput, aiExportProgress, onExportInitiated } = props;
  const [t] = useTranslation();
  const [filterObj, setFilterObj] = useState({ fromDate: null, toDate: null });
  const [errorMessage, setErrorMessage] = useState("");
  const [downloadRunning, setDownloadRunning] = useState(false);
  const { showProgressToast, showToastv2 } = useContext(toastContext);

  const parseDateString = (ddMMyyyy) => {
    if (!ddMMyyyy) return null;
    const [d, m, y] = ddMMyyyy.split("/");
    return new Date(y, m - 1, d);
  };

  const formatDateForApi = (date) => {
    if (!date) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const checkForDateNotNull = (fromDate, toDate) => {
    return !fromDate || !toDate;
  };

  const checkForDateRange = (fromStr, toStr) => {
    const [fd, fm, fy] = fromStr.split("/");
    const [td, tm, ty] = toStr.split("/");
    const from = new Date(fy, fm - 1, fd);
    const to = new Date(ty, tm - 1, td);
    return from > to;
  };

  const checkForMaxRange = (fromStr, toStr) => {
    const [fd, fm, fy] = fromStr.split("/");
    const [td, tm, ty] = toStr.split("/");
    const from = new Date(fy, fm - 1, fd);
    const to = new Date(ty, tm - 1, td);
    const oneDay = 24 * 60 * 60 * 1000;
    const diffDays = Math.round(Math.abs(to - from) / oneDay);
    return diffDays > 45;
  };

  const buildPayload = (values) => {
    return {
      fromDate: formatDateForApi(filterObj.fromDate),
      toDate: formatDateForApi(filterObj.toDate),
      patientId: aiRequestsFilterObj.patientId || null,
      patientName: aiRequestsFilterObj.patientName || null,
      filterInput: filterInput || null,
      destination: aiRequestsFilterObj.destination || null,
      jobId: aiRequestsFilterObj.jobId || null,
      sendingStatus: aiRequestsFilterObj.sendingStatus || null,
      responseStatus: aiRequestsFilterObj.responseStatus || null,
      pageNumber: 0,
      pageSize: 200,
    };
  };

  const hideModal = () => {
    onClose();
    setValues({
      fromDate: "",
      toDate: "",
    });
    setErrorMessage("");
  };

  const { values, errors, handleSubmit, setValues, setErrors } = useFormik({
    initialValues: {
      fromDate: null,
      toDate: null,
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, actions) => {
      const payload = buildPayload(values);
      setErrorMessage("");
      setErrors({});

      if (!payload.fromDate && !payload.toDate) {
        const fromError = t("export-csv.date-from-required") || "Request Created From date is required.";
        const toError = t("export-csv.date-to-required") || "Request Created To date is required.";
        setErrors({ fromDate: fromError, toDate: toError });
        return;
      }
      if (!payload.fromDate) {
        const fromError = t("export-csv.date-from-required") || "Request Created From date is required.";
        setErrors({ fromDate: fromError });
        return;
      }
      if (!payload.toDate) {
        const toError = t("export-csv.date-to-required") || "Request Created To date is required.";
        setErrors({ toDate: toError });
        return;
      }

      if (checkForDateRange(payload.fromDate, payload.toDate)) {
        setErrorMessage(t("error.from-to-date-not-correct"));
        return;
      }

      if (checkForMaxRange(payload.fromDate, payload.toDate)) {
        setErrorMessage(t("error.date-difference-greater-than-31"));
        return;
      }

      if (aiExportProgress > 0 && aiExportProgress < 100) {
        showToastv2(TOAST_TYPE.INFO, "Your file is getting download. Please wait");
        return;
      }

      setDownloadRunning(true);

      const statusResponse = await aiExportCsvStatusAPI();
      if (statusResponse.status === 200 && statusResponse.data.data === "Processing") {
        showToastv2(TOAST_TYPE.INFO, "Request for Export CSV is already in process");
        setDownloadRunning(false);
        hideModal();
        return;
      }

      const response = await aiExportCsvInitiate(payload);
      if (response.status === 201) {
        const id = showProgressToast("Downloading file", 0);
        onExportInitiated(id, response.data.data.token);
        showToastv2(TOAST_TYPE.INFO, "Downloading started");
        setDownloadRunning(false);
        hideModal();
      } else if (response.status === 200 && response.data.data === "Success") {
        showToastv2(TOAST_TYPE.INFO, response.data.message || "Export request submitted");
        hideModal();
      } else if (response.status === 200 && response.data.data === "Error") {
        setDownloadRunning(false);
        setErrorMessage(response.data.message);
      } else {
        setDownloadRunning(false);
        setErrorMessage(response.data.message || "Failed to start download");
      }
    },
  });

  useEffect(() => {
    if (show) {
      setFilterObj({ fromDate: null, toDate: null });
      setValues({ fromDate: null, toDate: null });
      setErrorMessage("");
    }
  }, [show]);

  const DefaultButtonList = [
    { title: t("button.cancel"), onclick: hideModal },
  ];

  const PrimaryButtonList = [
    {
      title: t("button.download"),
      disabled: downloadRunning,
      onclick: handleSubmit,
    },
  ];

  const modalDetails = {
    header: (
      <ModalHeader
        updatedModal={true}
        heading={t("export-csv.title")}
        handleCancel={hideModal}
      />
    ),
    body: (
      <AiExportCsvBody
        values={values}
        handleChange={() => {}}
        handleSubmit={handleSubmit}
        errors={errors}
        filterObj={filterObj}
        setFilterObj={setFilterObj}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        setValues={setValues}
        setErrors={setErrors}
      />
    ),
    footer: (
      <ModalFooter
        DefaultButtonList={DefaultButtonList}
        PrimaryButtonList={PrimaryButtonList}
      />
    ),
    width: "600px",
  };

  return (
    <>
      {show && (
        <ModalMain
          updatedModal={true}
          modalDetails={modalDetails}
          showModal={show}
        />
      )}
    </>
  );
};

export default AiExportCsvModal;
